import { DEFAULT_PRIVACY_SELECTION } from '~/exports/scope'
import { useAsyncData } from '~/hooks/useAsyncData'
import { deliverWeeklyReport } from '~/services/exportService'
import {
  discardWaitingProject,
  endProject,
  extendProject,
  getProjectEndState,
  getProjectStartState,
  listProjectHistory,
  renderProjectSummaryExport,
  rescheduleProjectStart,
} from '~/services/projectService'
import { todayFor } from '~/services/weightService'
import { useDatabase } from '~/storage/DatabaseContext'
import { STORE } from '~/storage/schema'
import { unwrapDate } from '~/types/dates'
import { ProjectSection } from './ProjectSection'

/**
 * The project end decision and project history, with their own data.
 *
 * The same shape as `PhaseHistoryEditor` one level up, and for the same reason:
 * this needs *every* project rather than the active one, so it reads its own
 * rather than being folded into the record-shaped editable-records reader.
 *
 * `STORE.projects` is the only invalidation key. Every write here is an explicit
 * save that closes its form, so re-reading afterwards is what should happen —
 * unlike the weekly wizard, where autosave made re-reading destroy the user's
 * position (D-073).
 */
export function ProjectEditor({ onSaved }: { onSaved: () => void }) {
  const db = useDatabase()

  const { state, reload } = useAsyncData(async () => {
    const now = new Date()
    const [endState, startState, projects] = await Promise.all([
      getProjectEndState(db, now),
      getProjectStartState(db, now),
      listProjectHistory(db),
    ])
    return { endState, startState, projects, today: unwrapDate(todayFor(now)) }
  }, [STORE.projects])

  if (state.status !== 'ready') return null

  const { endState, startState, projects, today } = state.value
  if (!endState.ok || !startState.ok || !projects.ok) return null

  /* A write that failed must never look like one that worked, so each handler
     returns the message on failure and refreshes only on success. */
  function afterWrite(failure: string | undefined): string | undefined {
    if (failure === undefined) {
      reload()
      onSaved()
    }
    return failure
  }

  const project = endState.value.project

  return (
    <ProjectSection
      endState={endState.value}
      startState={startState.value}
      projects={projects.value}
      today={today}
      onExtend={async (targetDate) => {
        if (project === undefined) return 'There is no project to extend.'
        const written = await extendProject(db, project.id, targetDate, new Date())
        return afterWrite(written.ok ? undefined : written.error.message)
      }}
      onEnd={async (request) => {
        if (project === undefined) return 'There is no project to end.'
        const written = await endProject(db, project.id, request, new Date())
        return afterWrite(written.ok ? undefined : written.error.message)
      }}
      /* Both refuse in the service once the project has started (D-107 §5, §6),
         so a stale screen cannot reschedule or discard a running project. */
      onReschedule={async (startDate) => {
        if (project === undefined) return 'There is no project to reschedule.'
        const written = await rescheduleProjectStart(db, project.id, startDate, new Date())
        return afterWrite(written.ok ? undefined : written.error.message)
      }}
      onDiscard={async () => {
        if (project === undefined) return 'There is no project to discard.'
        const done = await discardWaitingProject(db, project.id, new Date())
        return afterWrite(done.ok ? undefined : done.error.message)
      }}
      onExportSummary={async (finished) => {
        /* The cautious defaults, exactly as the weekly export uses them: no
           notes, no exact dates, until the user says otherwise (D-019). */
        const file = await renderProjectSummaryExport(
          db,
          finished,
          DEFAULT_PRIVACY_SELECTION,
          new Date(),
        )
        if (!file.ok) return
        await deliverWeeklyReport(file.value)
      }}
    />
  )
}
